import { useMemo } from "react";

import { keyBy } from "lodash-es";

import { FlowVersion, FlowVersionStatus } from "src/api/flowTypes";
import { getColorByHex } from "src/design-system/LegendIcon";
import { type LegendIconColor } from "src/design-system/utils/colorPalette";
import { getVersionsColors } from "src/utils/getVersionsColors";

type UseVersionPickerOptions = {
  // Flow versions from context (for names and metadata)
  flowVersions: FlowVersion[];
  // Array of version IDs to display in picker (can be empty if no versions available yet)
  allAvailableVersions: string[];
};

type UseVersionPickerReturn = {
  colors: Record<string, string>;
  options: Array<{
    key: string;
    value: string;
    color?: LegendIconColor;
  }>;
};

/**
 * Custom hook for computing version picker display data
 *
 * Takes a list of available version IDs and flow version metadata,
 * and computes:
 * - Stable color scheme for each version (based on all published/archived versions)
 * - Dropdown options with names and colors (sorted by name)
 *
 * Colors are computed from ALL published/archived versions to ensure consistency
 * regardless of time window or data availability. This means a version always has
 * the same color across different time ranges.
 *
 * This hook is reused across both Iceberg and Aurora dashboards.
 *
 * @param flowVersions - Array of flow versions from context (for names/metadata)
 * @param allAvailableVersions - Array of version IDs to display in picker
 * @returns colors and options for version picker UI
 */
export const useVersionPicker = ({
  flowVersions,
  allAvailableVersions,
}: UseVersionPickerOptions): UseVersionPickerReturn => {
  const publishedOrArchivedVersions = useMemo(
    () =>
      flowVersions.filter(
        (v) =>
          v.status === FlowVersionStatus.PUBLISHED ||
          v.status === FlowVersionStatus.ARCHIVED,
      ),
    [flowVersions],
  );

  // Generate stable colors from ALL published/archived versions
  const colors = useMemo(() => {
    return getVersionsColors(publishedOrArchivedVersions);
  }, [publishedOrArchivedVersions]);

  // Look up dictionary for version names
  const versionsById = useMemo(() => {
    return keyBy(publishedOrArchivedVersions, "id");
  }, [publishedOrArchivedVersions]);

  // Generate version picker options with names and colors, sorted by name
  const options = useMemo(
    () =>
      allAvailableVersions
        .map((versionId) => ({
          key: versionId,
          value: versionsById[versionId]?.name ?? versionId,
          color: colors[versionId]
            ? getColorByHex(colors[versionId])
            : undefined,
        }))
        .sort((a, b) => a.value.localeCompare(b.value)),
    [allAvailableVersions, versionsById, colors],
  );

  return {
    colors,
    options,
  };
};
